import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@/hooks/use-theme";
import { formatCurrency } from "@/lib/format";
import type { DayPoint } from "@/hooks/use-dashboard";

/**
 * Pedidos e receita por dia.
 *
 * As cores saem dos tokens de marca, e não de uma paleta do Recharts: o
 * gráfico precisa ler igual no tema claro e no escuro, e uma cor fixa serve
 * bem a um e mal ao outro.
 */
export function OrdersChart({ data }: { data: DayPoint[] }) {
  const { isDark } = useTheme();
  const grid = isDark ? "rgba(255,255,255,0.08)" : "rgba(41,29,25,0.10)";
  const axis = isDark ? "#C9B9B9" : "#332F2F";

  // Num período longo, um rótulo por dia vira uma faixa preta ilegível.
  const tickInterval = data.length > 60 ? Math.floor(data.length / 12) : data.length > 31 ? 4 : 2;

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C76F43" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#C76F43" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis
            dataKey="label"
            interval={tickInterval}
            tick={{ fill: axis, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fill: axis, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={56}
            tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
          />
          <Tooltip
            contentStyle={{
              background: isDark ? "#262626" : "#FFFFFF",
              border: `1px solid ${isDark ? "#332F2F" : "#C9B9B9"}`,
              borderRadius: 10,
              fontSize: 13,
              color: isDark ? "#FFFFFF" : "#291D19",
            }}
            formatter={(value: number, name) =>
              name === "revenue"
                ? [formatCurrency(value), "Receita entregue"]
                : [value, "Pedidos"]
            }
          />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke="#C76F43"
            strokeWidth={2}
            fill="url(#revenueFill)"
          />
          <Area
            type="monotone"
            dataKey="orders"
            stroke="#984C27"
            strokeWidth={1.5}
            fillOpacity={0}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
